import {
  ExpressionValue,
  IElement,
  OneOfPropertyElements,
} from '@shared/models/project.model';
import {
  ElementQuantityName,
  FactoryQuantityRecord,
  IElementQuantityProperty,
  IFactoryQuantityProperty,
} from '@shared/models/element-quantities.model';
import {
  PropertyChange,
  applyElementPropertyChanges,
  getElementProperties,
  isElementExpressionProperty,
} from '../element-property.helpers';
import { getElementQuantityProperties } from '../element-quantity.helpers';
import { getItemByIdOrName } from '../array.helpers';
import {
  isExpressionValue,
  type ExpressionValueFactoryType,
} from '../expression-factory.helpers';
import { isNonLiteralQuantityExpression } from '../element-quantity-value-override.helpers';
import { isElement } from '../recursive-element.helpers';
import { getElementCategoryId } from '../element-category-id.helpers';
import { ElementCategoryID } from '@shared/models/element-categories.model';
import { computeCategoryPropertyUpdates } from './compute-enrichment-updates.helpers';
import type { ProjectChange, ProjectChangeMap } from './update-engine.models';
import { ProjectChangeType } from './update-engine.models';
import { IElementProperty } from '@shared/models/element-property.model';
import { guards } from '../typeguards.helpers';

/** Row from {@link FactoryQuantityRecord} or a full factory quantity object from an array patch */
type QuantityChangesEntry =
  IFactoryQuantityProperty | FactoryQuantityRecord[ElementQuantityName];

/** Accepts element property counts and quantity/factory row payloads */
type ExpressionCountPatchSource =
  | IElementProperty['count']
  | ExpressionValueFactoryType
  | QuantityChangesEntry
  | undefined;

type RegularPropertyProjectChange =
  | ProjectChangeMap[ProjectChangeType.ElementPropertyResolved]
  | ProjectChangeMap[ProjectChangeType.ElementPropertyExpression]
  | ProjectChangeMap[ProjectChangeType.ElementPropertyValue]
  | ProjectChangeMap[ProjectChangeType.ElementPropertyCount];
type QuantityProjectChange =
  | ProjectChangeMap[ProjectChangeType.ElementQuantityResolved]
  | ProjectChangeMap[ProjectChangeType.ElementQuantityExpression]
  | ProjectChangeMap[ProjectChangeType.ElementQuantityFallbackExpression]
  | ProjectChangeMap[ProjectChangeType.ElementQuantityValue]
  | ProjectChangeMap[ProjectChangeType.ElementQuantityCount];

type PropertyCountPatchSource =
  IElementProperty['count'] | Partial<ExpressionValue> | undefined;

type WholePropertyCountResult =
  { type: 'count'; count: IElementProperty['count'] } | { type: 'invalid' };

const isExpressionValuePatch = (
  count: unknown,
): count is Partial<ExpressionValue> =>
  guards.is.object(count) &&
  !Array.isArray(count) &&
  ('resolved' in count ||
    'expression' in count ||
    'formatted' in count ||
    'value' in count);

const getWholePropertyCount = (
  count: PropertyCountPatchSource,
): WholePropertyCountResult => {
  if (!isExpressionValuePatch(count)) {
    return { type: 'count', count };
  }
  if (isExpressionValue(count)) {
    return { type: 'count', count };
  }
  return { type: 'invalid' };
};

/**
 * Builds a partial {@link ExpressionValue} patch from a property/quantity `count` payload.
 * {@link isExpressionValue} requires expression, resolved, and formatted to be defined; the
 * expression panel may still emit objects that only set a subset (e.g. resolved-only), which
 * must become granular quantity / property patches.
 *
 * @param count - Count from an element property or quantity row
 * @returns Fields to merge; empty object when nothing mappable
 */
const expressionValuePatchFromCountPayload = (
  count: ExpressionCountPatchSource,
): Partial<ExpressionValue> => {
  const value: Partial<ExpressionValue> = {};

  if (isExpressionValue(count)) {
    if (count.resolved !== undefined) value.resolved = count.resolved;
    if (count.expression !== undefined) value.expression = count.expression;
    if (count.formatted !== undefined) value.formatted = count.formatted;
    // Always carry `value` (even when undefined) so clearing only the numeric override on a
    // quantity that keeps its expression propagates through `mergeQuantityEntry`
    // (`{ ...base, ...patch }` would otherwise let the old `value` survive).
    value.value = count.value;
    return value;
  }

  if (typeof count === 'number') {
    value.resolved = count;
    return value;
  }

  // Partial object (not passing isExpressionValue) — copy any present ExpressionValue keys
  if (guards.is.object(count) && !Array.isArray(count)) {
    const o = count as Record<string, unknown>;
    if (o.resolved !== undefined) value.resolved = o.resolved as number;
    if (o.expression !== undefined) value.expression = o.expression as string;
    if (o.formatted !== undefined) value.formatted = o.formatted as string;
    if (o.value !== undefined) value.value = o.value as number;
  }

  return value;
};

/**
 * Convert a quantity expression patch to field-level {@link ProjectChange} items.
 *
 * @param elementId - Element that owns the quantity
 * @param quantityRowBeforePatch - Persisted quantity row before applying `value` (supplies
 *   `quantityName` and baseline for diffs). Expression formula patches always emit
 *   {@link ProjectChangeType.ElementQuantityExpression}; {@link elementQuantityExpressionToChange}
 *   bootstraps `count` from `fallbackCount` when the row has no explicit `count` yet.
 * @param value - Expression fields emitted by the input
 *
 * Does not emit {@link ProjectChangeType.ElementQuantityFormatted}; applying
 * {@link ProjectChangeType.ElementQuantityResolved} sets `formatted` in the update engine.
 */
const expressionValuePatchToQuantityProjectChanges = (
  elementId: string,
  quantityRowBeforePatch: IElementQuantityProperty,
  value: Partial<ExpressionValue>,
  currentCount?: ExpressionValue,
): QuantityProjectChange[] => {
  const changes: QuantityProjectChange[] = [];
  const quantityName = quantityRowBeforePatch.name as ElementQuantityName;
  const emitsValueOverride =
    'value' in value && value.value !== currentCount?.value;

  if (
    value.resolved !== undefined &&
    value.resolved !== currentCount?.resolved &&
    !emitsValueOverride
  ) {
    changes.push({
      type: ProjectChangeType.ElementQuantityResolved,
      elementId,
      quantityName,
      value: value.resolved,
    });
  }

  const skipExpressionChangeForInheritValueOverride =
    emitsValueOverride &&
    !isExpressionValue(quantityRowBeforePatch.count) &&
    isNonLiteralQuantityExpression(currentCount?.expression) &&
    !isNonLiteralQuantityExpression(value.expression);

  if (
    value.expression !== undefined &&
    value.expression !== currentCount?.expression &&
    !skipExpressionChangeForInheritValueOverride
  ) {
    // User edits always target count — fallbackCount is managed programmatically only.
    // elementQuantityExpressionToChange bootstraps count when the row has none yet.
    changes.push({
      type: ProjectChangeType.ElementQuantityExpression,
      elementId,
      quantityName,
      expression: value.expression,
    });
  }

  if ('value' in value && value.value !== currentCount?.value) {
    changes.push({
      type: ProjectChangeType.ElementQuantityValue,
      elementId,
      quantityName,
      overrideValue: value.value,
    });
  }

  return changes;
};

const getQuantityExpressionCount = (
  quantity: QuantityChangesEntry | IElementQuantityProperty | undefined,
): ExpressionValue | undefined => {
  if (!guards.is.object(quantity)) return undefined;
  const count = 'count' in quantity ? quantity.count : quantity;
  if (isExpressionValue(count)) return count;
  if (
    'fallbackCount' in quantity &&
    isExpressionValue(quantity.fallbackCount)
  ) {
    return quantity.fallbackCount;
  }
};

/**
 * Reads the count payload from a {@link FactoryQuantityRecord} value or factory quantity row.
 * Record values may be bare primitives, expression patches, or full property objects.
 */
const getQuantityCountFromChangesEntry = (
  entry: QuantityChangesEntry,
): PropertyCountPatchSource => {
  if (!guards.is.object(entry)) return entry as PropertyCountPatchSource;
  if ('count' in entry) {
    // Factory record values use `count`; TS keeps the whole row in the indexed union.
    return entry.count as PropertyCountPatchSource;
  }
  if (isExpressionValuePatch(entry)) return entry;
  return undefined;
};

/**
 * Map a quantity row `count` payload to {@link QuantityProjectChange} items.
 * Expression patches are split field-by-field; switch/select primitives become
 * {@link ProjectChangeType.ElementQuantityCount}.
 */
const quantityCountPayloadToProjectChanges = (
  elementId: string,
  quantityRow: IElementQuantityProperty,
  count: PropertyCountPatchSource,
  expressionCountBaseline?: ExpressionValue,
): QuantityProjectChange[] => {
  if (count === undefined) {
    if (quantityRow.count === undefined) return [];
    return [
      {
        type: ProjectChangeType.ElementQuantityCount,
        elementId,
        quantityName: quantityRow.name as ElementQuantityName,
        count: undefined,
      },
    ];
  }

  const value = expressionValuePatchFromCountPayload(count);

  if (Object.keys(value).length === 0) {
    // Switch/select quantity counts (e.g. repeating_items) are primitives, not expressions.
    const wholeCount = getWholePropertyCount(count);
    if (wholeCount.type === 'invalid') return [];
    if (wholeCount.count === quantityRow.count) return [];
    return [
      {
        type: ProjectChangeType.ElementQuantityCount,
        elementId,
        quantityName: quantityRow.name as ElementQuantityName,
        count: wholeCount.count,
      },
    ];
  }

  return expressionValuePatchToQuantityProjectChanges(
    elementId,
    quantityRow,
    value,
    expressionCountBaseline ?? getQuantityExpressionCount(quantityRow),
  );
};

/**
 * Applies {@link PropertyChange} items to a copy of the element's property list (in-memory only).
 * Used to preview the post-edit property state so we can derive category structural updates.
 */
const previewElementPropertyChanges = (
  element: IElement,
  changes: PropertyChange[],
): IElement => {
  let props = getElementProperties(element);
  for (const change of changes) {
    // skipValidation: preview can be transiently inconsistent (e.g. metal material
    // flipped to Aluminum while form still carries a Copper-only count) until enrich runs.
    props = applyElementPropertyChanges(
      props,
      { skipValidation: true },
      change,
    );
  }
  return { ...element, properties: props };
};

/**
 * Convert legacy {@link PropertyChange} items to typed {@link ProjectChange} items
 * by diffing against the element's current property state.
 *
 * Handles both regular element properties and quantity properties, producing the
 * narrowest possible change types ({@link ProjectChangeType.ElementPropertyCount},
 * {@link ProjectChangeType.ElementQuantityResolved}, etc.).
 *
 * After element property edits, appends {@link ProjectChangeType.ElementCategoryEnrich} when
 * the category template would add or remove properties (e.g. COP and heat source when switching
 * to heat pump) so the client persists structural category updates without waiting for compute.
 *
 * Changes that cannot be mapped are silently skipped.
 */
export const propertyChangesToProjectChanges = (
  element: OneOfPropertyElements,
  changes: PropertyChange[],
): ProjectChange[] => {
  const quantities: IElementQuantityProperty[] = isElement(element)
    ? Object.values(getElementQuantityProperties(element))
    : [];

  const baseChanges = changes.flatMap((change) => {
    const quantityMatch = getItemByIdOrName(quantities, change, false);
    if (quantityMatch) {
      return quantityChangeToProjectChange(element.id, quantityMatch, change);
    }
    return regularPropertyChangeToProjectChange(element, change);
  });

  // Skip when there is no element category, no element property edits, or only quantity edits.
  const propertyOnlyChanges = changes.filter(
    (c) => !getItemByIdOrName(quantities, c, false),
  );
  const categoryId = getElementCategoryId(element);
  if (
    !isElement(element) ||
    propertyOnlyChanges.length === 0 ||
    categoryId === ElementCategoryID.None
  ) {
    return baseChanges;
  }

  // Preview the post-edit element so category enrichment sees the new property values
  // (e.g. heating type = heat pump). computeCategoryPropertyUpdates returns the structural
  // add/remove diff that the same baseline enrichment pipeline would emit later.
  const preview = previewElementPropertyChanges(element, propertyOnlyChanges);
  const structural = computeCategoryPropertyUpdates(preview);

  return [...baseChanges, ...structural];
};

/**
 * Convert a quantity {@link PropertyChange} to field-level quantity changes.
 */
const quantityChangeToProjectChange = (
  elementId: string,
  _quantity: IElementQuantityProperty,
  change: PropertyChange,
): QuantityProjectChange[] => {
  if (!('count' in change)) return [];

  return quantityCountPayloadToProjectChanges(
    elementId,
    _quantity,
    change.count,
    getQuantityExpressionCount(_quantity),
  );
};

/**
 * Convert a regular (non-quantity) {@link PropertyChange} to one or more typed
 * {@link ProjectChange} items by comparing the change against the current property.
 *
 * A change may carry `count`, `fallbackCount`, or both. `fallbackCount` is normally
 * managed by inheritance in the pipeline; callers pass it explicitly only to drop a
 * fallback that can no longer be inherited.
 */
const regularPropertyChangeToProjectChange = (
  element: OneOfPropertyElements,
  change: PropertyChange,
): ProjectChange[] => {
  const properties = getElementProperties(element);
  const property = getItemByIdOrName(properties, change, false);
  if (!property) return [];

  if (change.name != null && change.name !== property.name) {
    return [
      {
        type: ProjectChangeType.ElementPropertyRename,
        elementId: element.id,
        propertyName: property.name,
        name: change.name,
      },
    ];
  }

  const changes: ProjectChange[] = [];

  if ('count' in change) {
    const newCount = change.count;
    // undefined count = "clear count, fall back to fallbackCount"
    if (newCount !== undefined || property.count !== undefined) {
      changes.push(
        ...regularPropertyCountToProjectChanges(element.id, property, newCount),
      );
    }
  }

  if ('fallbackCount' in change) {
    const fallback = getWholePropertyCount(
      change.fallbackCount as PropertyCountPatchSource,
    );
    if (
      fallback.type === 'count' &&
      fallback.count !== property.fallbackCount
    ) {
      changes.push({
        type: ProjectChangeType.ElementPropertyFallbackCount,
        elementId: element.id,
        propertyName: property.name,
        fallbackCount: fallback.count,
      });
    }
  }

  return changes;
};

/**
 * Convert count edits to narrow {@link ProjectChange} items when an existing expression
 * count can be patched field-by-field. Whole-count changes are still required for
 * fallback-only expression properties and non-expression properties.
 *
 * Does not emit {@link ProjectChangeType.ElementPropertyFormatted}; applying
 * {@link ProjectChangeType.ElementPropertyResolved} sets `formatted` in the update engine.
 */
const regularPropertyCountToProjectChanges = (
  elementId: string,
  property: IElementProperty,
  newCount: PropertyCountPatchSource,
): RegularPropertyProjectChange[] => {
  if (
    !isElementExpressionProperty(property) ||
    !property.count ||
    !isExpressionValuePatch(newCount)
  ) {
    const wholeCount = getWholePropertyCount(newCount);
    if (wholeCount.type === 'invalid') return [];
    if (wholeCount.count === property.count) return [];
    return [
      {
        type: ProjectChangeType.ElementPropertyCount,
        elementId,
        propertyName: property.name,
        count: wholeCount.count,
      },
    ];
  }

  const currentCount = property.count;
  const changes: RegularPropertyProjectChange[] = [];
  const emitsValueOverride =
    'value' in newCount && newCount.value !== currentCount.value;

  if (
    newCount.resolved !== undefined &&
    newCount.resolved !== currentCount.resolved &&
    !emitsValueOverride
  ) {
    changes.push({
      type: ProjectChangeType.ElementPropertyResolved,
      elementId,
      propertyName: property.name,
      value: newCount.resolved,
    });
  }

  if (
    newCount.expression !== undefined &&
    newCount.expression !== currentCount.expression
  ) {
    changes.push({
      type: ProjectChangeType.ElementPropertyExpression,
      elementId,
      propertyName: property.name,
      expression: newCount.expression,
    });
  }

  if ('value' in newCount && newCount.value !== currentCount.value) {
    changes.push({
      type: ProjectChangeType.ElementPropertyValue,
      elementId,
      propertyName: property.name,
      overrideValue: newCount.value,
    });
  }

  return changes;
};

/**
 * Convert {@link FactoryQuantityRecord} or {@link IFactoryQuantityProperty}[] to
 * field-level quantity changes.
 *
 * Each quantity entry with an {@link ExpressionValue} or numeric count produces
 * granular {@link ProjectChange} items. Used by {@link useUpdateQuantity}.
 */
export const quantityChangesToProjectChanges = (
  element: IElement,
  changes: FactoryQuantityRecord | IFactoryQuantityProperty[],
): ProjectChange[] => {
  const entries: Array<[string, QuantityChangesEntry]> = Array.isArray(changes)
    ? changes.map((c) => [c.name, c])
    : (Object.entries(changes) as Array<[string, QuantityChangesEntry]>);

  return entries.flatMap(([name, entry]) => {
    const quantityName = name as ElementQuantityName;
    const existingRow = element.quantity?.[quantityName];
    const count = getQuantityCountFromChangesEntry(entry);

    const quantityRowBeforePatch =
      existingRow ?? ({ name: quantityName } as IElementQuantityProperty);

    return quantityCountPayloadToProjectChanges(
      element.id,
      quantityRowBeforePatch,
      count,
      getQuantityExpressionCount(existingRow ?? entry),
    );
  });
};
